import React, { useState } from "react";
import { Trash2, AlertTriangle, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * DeleteAccountCard — GDPR / CCPA compliant delete-my-account
 * surface. Requires the user to retype their email as a second-factor
 * against XSS-triggered deletion, and shows an explicit "this
 * anonymises your content" disclaimer so nobody misunderstands the
 * scope.
 *
 * Backend: DELETE /api/auth/me — see /app/backend/routes/auth.py.
 */
export default function DeleteAccountCard() {
  const { user, logout } = useAuth();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const downloadData = async () => {
    setDownloading(true);
    try {
      const { data } = await api.post("/auth/data-request");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tapin-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success("Your data export was downloaded.");
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't export data.");
    } finally { setDownloading(false); }
  };

  const disabled = busy || !confirm || confirm.trim().toLowerCase() !== (user?.email || "").toLowerCase();

  const submit = async () => {
    if (disabled) return;
    if (!window.confirm("Delete your Tap In account? This can't be undone.")) return;
    setBusy(true);
    try {
      await api.delete("/auth/me", { data: { confirm_email: confirm.trim() } });
      toast.success("Account deleted. Goodbye 👋");
      try { logout?.(); } catch { /* logout handles storage */ }
      setTimeout(() => { window.location.href = "/"; }, 900);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't delete account.");
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <section data-testid="delete-account-card" className="tv-glass rounded-2xl p-4 sm:p-5 mb-6 border border-red-500/20">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="inline-flex items-center gap-1 text-red-400 text-[10px] uppercase tracking-widest font-black mb-1">
            <AlertTriangle className="w-3 h-3" /> Danger zone
          </div>
          <div className="font-display font-black text-white text-lg">Delete your account</div>
          <div className="text-white/50 text-xs mt-0.5">
            Permanent. Your profile is wiped, sessions destroyed, and any content is anonymised.
          </div>
        </div>
        {!expanded && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={downloadData}
              disabled={downloading}
              data-testid="data-export-btn"
              className="text-white/70 hover:text-white text-xs font-black uppercase tracking-wider inline-flex items-center gap-1 border border-white/15 bg-white/5 hover:bg-white/10 rounded-full px-3 py-1.5"
              title="GDPR Article 15 — download everything we hold about you"
            >
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              {downloading ? "Preparing…" : "Download my data"}
            </button>
            <button
              type="button"
              onClick={() => setExpanded(true)}
              data-testid="delete-account-expand"
              className="text-red-400 hover:text-red-300 text-xs font-black uppercase tracking-wider inline-flex items-center gap-1"
            ><Trash2 className="w-3.5 h-3.5" /> Delete…</button>
          </div>
        )}
      </div>
      {expanded && (
        <div className="mt-3 space-y-3">
          <div className="text-xs text-white/70 bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2">
            To confirm, type your email <span className="font-black text-white">{user.email}</span> below. This can&apos;t be undone.
          </div>
          <input
            type="email"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="your@email.com"
            data-testid="delete-account-confirm"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-red-500 placeholder:text-white/25"
          />
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:justify-end">
            <button
              type="button"
              onClick={() => { setExpanded(false); setConfirm(""); }}
              data-testid="delete-account-cancel"
              className="text-white/60 hover:text-white text-xs font-bold uppercase tracking-wider px-4 py-2"
            >Cancel</button>
            <button
              type="button"
              onClick={submit}
              disabled={disabled}
              data-testid="delete-account-submit"
              className="bg-red-500 hover:bg-red-600 text-white font-black text-xs uppercase tracking-wider px-5 py-2 rounded-full disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {busy ? "Deleting…" : "Yes, delete my account"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
